import axios from 'axios';

const STATS_API_URL = `${process.env.REACT_APP_API_URL}/stats/`;

const getAuthHeadersRequired = () => {
  const token = localStorage.getItem('token');
  if (!token) {
    console.error('No se encontró un token válido.');
    throw new Error('Usuario no autenticado');
  }
  return { Authorization: `Bearer ${token}` };
};

// endpoints públicos (no requiere token)
const getAuthHeadersOptional = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const handleError = (error) => {
  if (error.response?.data?.message === 'Invalid token') {
    console.error('Token inválido. Eliminando token de localStorage.');
    localStorage.removeItem('token');
    throw new Error('Token inválido. Por favor, inicie sesión nuevamente.');
  } else {
    console.error(error.response?.data || error.message);
    throw error;
  }
};

const buildRangeParams = ({ days, from, to, limit, includeAnte, minPlays } = {}) => {
  const params = {};
  if (from && to) {
    params.from = from;
    params.to = to;
  } else if (typeof days === 'number') {
    params.days = days;
  }
  if (typeof limit === 'number') params.limit = limit;
  if (typeof includeAnte !== 'undefined') params.includeAnte = includeAnte ? 1 : 0;
  if (typeof minPlays === 'number') params.minPlays = minPlays;
  return params;
};

/** 1) Más jugados */
export const getMostPlayedGames = async ({ days = 30, from, to, limit = 1000, includeAnte = false } = {}) => {
  try {
    const headers = getAuthHeadersRequired();
    const params = buildRangeParams({ days, from, to, limit, includeAnte });
    const { data } = await axios.get(`${STATS_API_URL}games/most-played`, { headers, params });
    return data;
  } catch (error) { handleError(error); }
};

/** 2) Más populares */
export const getMostPopularGames = async ({ days = 30, from, to, limit = 1000, includeAnte = false } = {}) => {
  try {
    const headers = getAuthHeadersRequired();
    const params = buildRangeParams({ days, from, to, limit, includeAnte });
    const { data } = await axios.get(`${STATS_API_URL}games/most-popular`, { headers, params });
    return data;
  } catch (error) { handleError(error); }
};

/** 3) Más rentables para la casa (admin) */
export const getMostProfitableGames = async ({ days = 30, from, to, limit = 1000 } = {}) => {
  try {
    const headers = getAuthHeadersRequired();
    const params = buildRangeParams({ days, from, to, limit });
    const { data } = await axios.get(`${STATS_API_URL}games/most-profitable`, { headers, params });
    return data;
  } catch (error) { handleError(error); }
};

/** 4) Top jugadores (admin) */
export const getTopPlayers = async ({ days = 30, from, to, limit = 100 } = {}) => {
  try {
    const headers = getAuthHeadersRequired();
    const params = buildRangeParams({ days, from, to, limit });
    const { data } = await axios.get(`${STATS_API_URL}players/top`, { headers, params });
    return data;
  } catch (error) { handleError(error); }
};

/** 5) Resumen por juego */
export const getGameSummary = async (gameId, { days = 30, from, to, limit = 10 } = {}) => {
  if (!gameId) throw new Error('gameId es requerido');
  try {
    const headers = getAuthHeadersRequired();
    const params = buildRangeParams({ days, from, to, limit });
    const { data } = await axios.get(`${STATS_API_URL}games/${gameId}/summary`, { headers, params });
    return data;
  } catch (error) { handleError(error); }
};

/** 6) Feed público: últimas jugadas ganadoras */
export const getRecentWinsPublic = async ({ gameId, minAmount, limit = 20 } = {}) => {
  try {
    const headers = getAuthHeadersOptional();
    const params = {};
    if (gameId) params.gameId = gameId;
    if (typeof minAmount === 'number') params.minAmount = minAmount;
    if (typeof limit === 'number') params.limit = limit;
    const { data } = await axios.get(`${STATS_API_URL}public/recent-wins`, { headers, params });
    return data;
  } catch (error) { handleError(error); }
};

/** 7) Ticker: últimas transacciones ( fix doble slash) */
export const getLastTransactions = async (limit = 100) => {
  try {
    const headers = getAuthHeadersOptional();
    const params = { limit };
    const { data } = await axios.get(`${STATS_API_URL}games/last-transactions`, { headers, params });
    return data;
  } catch (error) { handleError(error); }
};

/** 8) NUEVO: Juegos más favorables para el usuario */
export const getBestGamesForPlayers = async ({ days = 30, from, to, limit = 1000, minPlays = 10 } = {}) => {
  try {
    const headers = getAuthHeadersRequired();
    const params = buildRangeParams({ days, from, to, limit, minPlays });
    const { data } = await axios.get(`${STATS_API_URL}games/best-for-players`, { headers, params });
    return data;
  } catch (error) { handleError(error); }
};

/** 9) NUEVO: Ranking por ROI del jugador */
export const getTopPlayerROIByGame = async ({ days = 30, from, to, limit = 1000, minPlays = 10 } = {}) => {
  try {
    const headers = getAuthHeadersRequired();
    const params = buildRangeParams({ days, from, to, limit, minPlays });
    const { data } = await axios.get(`${STATS_API_URL}games/top-player-roi`, { headers, params });
    return data;
  } catch (error) { handleError(error); }
};

/** 10) NUEVO: El más rentable para el usuario (top 1) */
export const getMostProfitableForUser = async ({ days = 30, from, to, minPlays = 10 } = {}) => {
  try {
    const headers = getAuthHeadersRequired();
    const params = buildRangeParams({ days, from, to, limit: 1, minPlays });
    const { data } = await axios.get(`${STATS_API_URL}games/most-profitable-for-user`, { headers, params });
    return data?.[0] ?? null;
  } catch (error) { handleError(error); }
};

// Export agrupado
const statsService = {
  getMostPlayedGames,
  getMostPopularGames,
  getMostProfitableGames,
  getTopPlayers,
  getGameSummary,
  getRecentWinsPublic,
  getLastTransactions,
  getBestGamesForPlayers,
  getTopPlayerROIByGame,
  getMostProfitableForUser,
};

export default statsService;
